import React, { useState } from 'react';
import {
  Toolbar,
  Typography,
  Box,
  IconButton,
  Button,
  Container,
  Drawer,
  List,
  ListItemText,
  Badge,
  Paper,
  MenuItem,
  ClickAwayListener,
  Divider,
  useMediaQuery,
  useTheme,
  Collapse,
  ListItemButton,
  InputBase
} from "@mui/material";
import {
  Menu as MenuIcon,
  Search as SearchIcon,
  ShoppingBagOutlined as CartIcon,
  PersonOutline as AccountIcon,
  DashboardOutlined as DashboardIcon,
  KeyboardArrowDown,
  KeyboardArrowRight
} from '@mui/icons-material';
import { Link, useNavigate, useLocation } from 'react-router-dom';

import { useCart } from '../../context/CartContext';
import { useUser } from '../../context/UserContext';
import logo from '../../assets/ar-infotech-solutions.png';
import { products as allProducts } from '../../config/products';
import { getProductImage } from '../../utils/imageHelper';

// ─── Shared admin role check ────────────────────────────────────────
const ADMIN_ROLES = ["admin", "super admin"];

const checkIsStaff = (user) => {
  if (!user) return false;
  const rawRoles = user?.roles || user?.role || [];
  const userRolesList = Array.isArray(rawRoles)
    ? rawRoles
    : typeof rawRoles === "string"
      ? rawRoles.split(",").map(r => r.trim())
      : [];
  return userRolesList.some(r => ADMIN_ROLES.includes(String(r).trim().toLowerCase()));
};

// ─── Desktop Nav Components ─────────────────────────────────────────
const DesktopDropdownItem = ({ item, navigate }) => {
  const [open, setOpen] = useState(false);

  return (
    <Box
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      sx={{ position: 'relative' }}
    >
      <MenuItem
        onClick={() => navigate(item.path)}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          '&:hover': { backgroundColor: '#F9FAF4', color: '#0e6ada' },
          py: 1.5,
          px: 2
        }}
      >
        <Typography variant="body1" sx={{ fontWeight: 500 }}>{item.label}</Typography>
        {item.subDropdown && <KeyboardArrowRight fontSize="small" sx={{ ml: 2 }} />}
      </MenuItem>

      {item.subDropdown && open && (
        <Paper
          elevation={4}
          sx={{
            position: 'absolute',
            top: 0,
            left: '100%',
            minWidth: '200px',
            backgroundColor: '#FFF',
            borderRadius: '8px',
            py: 1,
            ml: 0.5,
            zIndex: 1300
          }}
        >
          {item.subDropdown.map((subItem) => (
            <MenuItem
              key={subItem.label}
              onClick={() => {
                navigate(subItem.path);
                setOpen(false);
              }}
              sx={{ '&:hover': { backgroundColor: '#F9FAF4', color: '#0e6ada' }, py: 1.5, px: 2 }}
            >
              <Typography variant="body1" sx={{ fontWeight: 500 }}>{subItem.label}</Typography>
            </MenuItem>
          ))}
        </Paper>
      )}
    </Box>
  );
};

const DesktopNavItem = ({ item, navigate, isActive }) => {
  const [open, setOpen] = useState(false);

  return (
    <Box
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}
    >
      <Button
        onClick={() => navigate(item.path)}
        endIcon={item.dropdown && <KeyboardArrowDown />}
        sx={{
          color: isActive ? '#0e6ada' : 'text.primary',
          fontWeight: 700,
          fontSize: '1rem',
          '&:hover': { color: '#0e6ada' },
          textTransform: 'capitalize',
          minWidth: 0,
          py: 2,
          borderBottom: isActive ? '2px solid #0e6ada' : '2px solid transparent',
          borderRadius: 0,
        }}
      >
        {item.label}
      </Button>

      {item.dropdown && open && (
        <Paper
          elevation={4}
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            minWidth: '240px',
            backgroundColor: '#FFF',
            borderRadius: '8px',
            py: 1,
            mt: -1,
            zIndex: 1200
          }}
        >
          {item.dropdown.map((dropItem) => (
            <DesktopDropdownItem key={dropItem.label} item={dropItem} navigate={navigate} />
          ))}
        </Paper>
      )}
    </Box>
  );
};

// ─── Mobile Nav Components ──────────────────────────────────────────
const MobileDropdownItem = ({ item, handleDrawerToggle, navigate }) => {
  const [open, setOpen] = useState(false);

  const handleClick = (e) => {
    if (item.subDropdown) {
      e.stopPropagation();
      setOpen(!open);
    } else {
      navigate(item.path);
      handleDrawerToggle();
    }
  };

  return (
    <>
      <ListItemButton
        onClick={handleClick}
        sx={{
          py: 1.5,
          px: 2,
          borderRadius: '8px',
          mb: 0.5,
          '&:hover': { bgcolor: '#F9FAF4', color: '#0e6ada' }
        }}
      >
        <ListItemText
          primary={item.label}
          primaryTypographyProps={{ fontWeight: 500, fontSize: '0.95rem' }}
        />
        {item.subDropdown && (open ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowRight fontSize="small" />)}
      </ListItemButton>

      {item.subDropdown && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding sx={{ pl: 3 }}>
            {item.subDropdown.map((subItem) => (
              <ListItemButton
                key={subItem.label}
                onClick={() => { navigate(subItem.path); handleDrawerToggle(); }}
                sx={{
                  py: 1.5,
                  px: 2,
                  borderRadius: '8px',
                  mb: 0.5,
                  '&:hover': { bgcolor: '#F9FAF4', color: '#0e6ada' }
                }}
              >
                <ListItemText
                  primary={subItem.label}
                  primaryTypographyProps={{ fontWeight: 400, fontSize: '0.9rem' }}
                />
              </ListItemButton>
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

const MobileNavItem = ({ item, handleDrawerToggle, navigate }) => {
  const [open, setOpen] = useState(false);

  const handleClick = (e) => {
    if (item.dropdown) {
      e.stopPropagation();
      setOpen(!open);
    } else {
      navigate(item.path);
      handleDrawerToggle();
    }
  };

  return (
    <>
      <ListItemButton
        onClick={handleClick}
        sx={{
          textAlign: 'left',
          py: 1.5,
          px: 2,
          borderRadius: '8px',
          mb: 0.5,
          '&:hover': { bgcolor: '#F9FAF4', color: '#0e6ada' }
        }}
      >
        <ListItemText
          primary={item.label}
          primaryTypographyProps={{ fontWeight: 700, fontSize: '1rem', color: 'text.primary' }}
        />
        {item.dropdown && (open ? <KeyboardArrowDown /> : <KeyboardArrowRight />)}
      </ListItemButton>

      {item.dropdown && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding sx={{ pl: 2 }}>
            {item.dropdown.map((dropItem) => (
              <MobileDropdownItem key={dropItem.label} item={dropItem} handleDrawerToggle={handleDrawerToggle} navigate={navigate} />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

// ─── Main Header ────────────────────────────────────────────────────
const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { totalItems } = useCart();
  const { user } = useUser();

  const isStaff = checkIsStaff(user);

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (query.length > 1) {
      const filtered = allProducts.filter(p => p.name.toLowerCase().includes(query.toLowerCase()));
      setSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  const executeSearch = (queryToSearch) => {
    const q = (queryToSearch !== undefined ? queryToSearch : searchQuery).trim();
    if (q) {
      navigate(`/search?q=${encodeURIComponent(q)}`);
      setShowSuggestions(false);
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      executeSearch();
    }
  };

  // Navigation Data Structure
  const navItems = [
    { label: 'Home', path: '/' },
    {
      label: 'Microsoft',
      path: '/shop?category=microsoft',
      dropdown: [
        {
          label: 'Windows',
          path: '/shop?category=windows',
          subDropdown: [
            { label: 'Windows 7', path: '/shop?category=windows-7' },
            { label: 'Windows 8', path: '/shop?category=windows-8' },
            { label: 'Windows 8.1', path: '/shop?category=windows-8-1' },
            { label: 'Windows 10', path: '/shop?category=windows-10' },
            { label: 'Windows 11', path: '/shop?category=windows-11' },
          ]
        },
        { label: 'MS Office', path: '/shop?category=ms-office' },
        { label: 'Servers', path: '/shop?category=servers' },
        { label: 'SQL Server', path: '/shop?category=sql-server' },
        { label: 'Visual Studio', path: '/shop?category=visual-studio' },
      ]
    },
    { label: 'Adobe', path: '/shop?category=adobe' },
    {
      label: 'Others',
      path: '/shop?category=others',
      dropdown: [
        { label: 'Antivirus', path: '/shop?category=antivirus' },
        { label: 'Auto Desk', path: '/shop?category=auto-desk' },
        { label: 'CorelDraw', path: '/shop?category=coreldraw' },
        { label: 'Tally', path: '/shop?category=tally' },
      ]
    },
    { label: 'Combo', path: '/shop?category=combo' },
    { label: 'Contact', path: '/contact' },
  ];

  // Check active route
  const isNavActive = (item) => {
    if (item.path === '/') return location.pathname === '/';
    const basePath = item.path.split('?')[0];
    return location.pathname === basePath || location.search.includes(item.path.split('?')[1] || '___');
  };

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const drawer = (
    <Box sx={{ p: isMobile ? 1 : 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: isMobile ? 1 : 2, px: 1 }}>
        <Box component="img" src={logo} alt="AR Infotech" sx={{ height: "45px", width: "auto" }} />
      </Box>
      {isStaff && (
        <Box sx={{ px: 1, mb: 1.5 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={() => {
              const isDashLoggedOut = localStorage.getItem('ar_dashboard_logged_out') === 'true';
              navigate(isDashLoggedOut ? '/dashboard/login' : '/dashboard');
              handleDrawerToggle();
            }}
            startIcon={<DashboardIcon />}
            sx={{
              bgcolor: '#1E40AF',
              color: '#FFF',
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: 1.5,
              py: 1,
              '&:hover': { bgcolor: '#172554' }
            }}
          >
            Admin Dashboard
          </Button>
        </Box>
      )}
      <Divider />
      <List>
        {navItems.map((item) => (
          <MobileNavItem key={item.label} item={item} handleDrawerToggle={handleDrawerToggle} navigate={navigate} />
        ))}
      </List>
    </Box>
  );

  return (
    <>
      <Box component="header" sx={{ position: 'sticky', top: 0, zIndex: 1100, backgroundColor: 'rgba(255, 255, 255, 0.97)', backdropFilter: 'blur(8px)', borderBottom: '1px solid #EAEAEA' }}>
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ justifyContent: 'space-between', minHeight: { xs: '58px', sm: '64px' } }}>
            {/* Left: Brand Logo */}
            <Box component={Link} to="/" sx={{ display: 'flex', alignItems: 'center', py: 0.5, textDecoration: 'none' }}>
              <Box component="img" src={logo} alt="AR Infotech" sx={{ height: isMobile ? "38px" : "55px", width: "auto" }} />
            </Box>

            {/* Center: Desktop Navigation */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 3, alignItems: 'center', height: '100%' }}>
              {navItems.map((item) => (
                <DesktopNavItem key={item.label} item={item} navigate={navigate} isActive={isNavActive(item)} />
              ))}
            </Box>

            {/* Right: Actions */}
            <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, alignItems: 'center' }}>
              {/* Desktop Search Bar (md and up) */}
              <Box sx={{ position: 'relative', display: { xs: 'none', md: 'block' } }}>
                <ClickAwayListener onClickAway={() => setShowSuggestions(false)}>
                  <Box>
                    <Box
                      component="form"
                      onSubmit={(e) => { e.preventDefault(); executeSearch(); }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        bgcolor: '#F3F4F6',
                        px: 1.5,
                        borderRadius: 2,
                        border: '1px solid #E5E7EB',
                        '&:focus-within': { borderColor: '#0E6ADA', bgcolor: '#FFF' }
                      }}
                    >
                      <InputBase
                        placeholder="Search for Products"
                        value={searchQuery}
                        onChange={handleSearchChange}
                        onKeyDown={handleSearchKeyDown}
                        sx={{ py: 0.6, width: { md: '180px', lg: '240px' }, fontSize: '0.88rem' }}
                      />
                      <IconButton size="small" type="submit" sx={{ p: 0.5, color: '#0E6ADA' }} aria-label="Search">
                        <SearchIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    {/* Desktop Suggestions Dropdown */}
                    {showSuggestions && suggestions.length > 0 && (
                      <Paper
                        elevation={4}
                        sx={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          mt: 1,
                          zIndex: 1200,
                          maxHeight: 300,
                          overflowY: 'auto',
                          borderRadius: 2
                        }}
                      >
                        <List disablePadding>
                          {suggestions.map((prod) => (
                            <React.Fragment key={prod.id}>
                              <ListItemButton
                                onClick={() => {
                                  navigate(`/product/${prod.slug}`);
                                  setShowSuggestions(false);
                                  setSearchQuery('');
                                }}
                                sx={{ py: 1.5 }}
                              >
                                <Box component="img" src={getProductImage(prod.image)} sx={{ width: 40, height: 40, objectFit: 'contain', mr: 2, p: 0.5, bgcolor: '#FFF', borderRadius: 1 }} />
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{prod.name}</Typography>
                                  <Typography variant="caption" color="primary.main" sx={{ fontWeight: 700 }}>₹{prod.price}</Typography>
                                </Box>
                              </ListItemButton>
                              <Divider component="li" />
                            </React.Fragment>
                          ))}
                        </List>
                      </Paper>
                    )}
                  </Box>
                </ClickAwayListener>
              </Box>

              {/* Cart Icon (Desktop/Tablet only; on mobile it lives in Bottom Nav) */}
              <IconButton
                color="inherit"
                onClick={() => navigate('/cart')}
                aria-label={`Shopping cart, ${totalItems} items`}
                sx={{
                  display: { xs: 'none', md: 'inline-flex' },
                  transition: 'transform 0.2s ease',
                  '&:hover': { transform: 'scale(1.08)' }
                }}
              >
                <Badge
                  badgeContent={totalItems}
                  color="secondary"
                  sx={{
                    '& .MuiBadge-badge': {
                      fontWeight: 700,
                      backgroundColor: '#0E6ADA',
                      color: '#FFF'
                    }
                  }}
                >
                  <CartIcon sx={{ fontSize: 28 }} />
                </Badge>
              </IconButton>

              {/* Admin Dashboard Pill */}
              {isStaff && (
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => {
                    const isDashLoggedOut = localStorage.getItem('ar_dashboard_logged_out') === 'true';
                    navigate(isDashLoggedOut ? '/dashboard/login' : '/dashboard');
                  }}
                  startIcon={<DashboardIcon sx={{ fontSize: 18 }} />}
                  sx={{
                    bgcolor: '#1E40AF',
                    color: '#FFF',
                    fontWeight: 700,
                    fontSize: '0.82rem',
                    borderRadius: '20px',
                    textTransform: 'none',
                    px: { xs: 1.2, sm: 1.8 },
                    py: 0.5,
                    boxShadow: '0 2px 8px rgba(30,64,175,0.3)',
                    '&:hover': { bgcolor: '#172554' },
                    display: { xs: 'none', md: 'flex' },
                    alignItems: 'center'
                  }}
                >
                  Dashboard
                </Button>
              )}

              {/* Account Icon (Desktop/Tablet only) */}
              <IconButton
                color="inherit"
                onClick={() => navigate(user ? '/account' : '/login')}
                aria-label={user ? 'My account' : 'Sign in'}
                sx={{ display: { xs: 'none', md: 'inline-flex' } }}
              >
                <AccountIcon sx={{ fontSize: 28 }} />
              </IconButton>

              {/* Mobile Menu Icon at Right Side */}
              <IconButton
                color="inherit"
                edge="end"
                onClick={handleDrawerToggle}
                aria-label="Open navigation menu"
                sx={{
                  display: { xs: 'flex', md: 'none' },
                  color: '#1E293B',
                  p: 1
                }}
              >
                <MenuIcon sx={{ fontSize: 28 }} />
              </IconButton>
            </Box>
          </Toolbar>

          {/* Mobile Search Field Row (Below Logo & Menu Icon toolbar on mobile) */}
          <Box sx={{ display: { xs: 'block', md: 'none' }, pb: 1.5, pt: 0.2 }}>
            <ClickAwayListener onClickAway={() => setShowSuggestions(false)}>
              <Box sx={{ position: 'relative' }}>
                <Box
                  component="form"
                  onSubmit={(e) => { e.preventDefault(); executeSearch(); }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    bgcolor: '#F1F5F9',
                    borderRadius: '10px',
                    border: '1px solid #E2E8F0',
                    px: 1.5,
                    py: 0.3,
                    '&:focus-within': {
                      borderColor: '#0E6ADA',
                      bgcolor: '#FFFFFF',
                      boxShadow: '0 0 0 2px rgba(14, 106, 218, 0.15)'
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <InputBase
                    placeholder="Search for Products"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onKeyDown={handleSearchKeyDown}
                    fullWidth
                    sx={{
                      fontSize: '0.9rem',
                      color: '#1E293B',
                      '& input::placeholder': {
                        color: '#64748B',
                        opacity: 1
                      }
                    }}
                  />
                  <IconButton
                    type="submit"
                    edge="end"
                    aria-label="Search for Products"
                    sx={{
                      color: '#0E6ADA',
                      p: '6px',
                      '&:hover': { bgcolor: 'rgba(14, 106, 218, 0.08)' }
                    }}
                  >
                    <SearchIcon fontSize="small" />
                  </IconButton>
                </Box>

                {/* Mobile Suggestions Dropdown */}
                {showSuggestions && suggestions.length > 0 && (
                  <Paper
                    elevation={4}
                    sx={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      mt: 0.5,
                      zIndex: 1300,
                      maxHeight: 280,
                      overflowY: 'auto',
                      borderRadius: 2,
                      border: '1px solid #E2E8F0',
                      bgcolor: '#FFFFFF'
                    }}
                  >
                    <List disablePadding>
                      {suggestions.map((prod) => (
                        <React.Fragment key={prod.id}>
                          <ListItemButton
                            onClick={() => {
                              navigate(`/product/${prod.slug}`);
                              setShowSuggestions(false);
                              setSearchQuery('');
                            }}
                            sx={{ py: 1.2, px: 2 }}
                          >
                            <Box
                              component="img"
                              src={getProductImage(prod.image)}
                              alt={prod.name}
                              sx={{
                                width: 38,
                                height: 38,
                                objectFit: 'contain',
                                mr: 1.5,
                                p: 0.5,
                                bgcolor: '#F8FAFC',
                                borderRadius: 1,
                                border: '1px solid #F1F5F9'
                              }}
                            />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, noWrap: true, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {prod.name}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#0E6ADA', fontWeight: 700 }}>
                                ₹{prod.price}
                              </Typography>
                            </Box>
                          </ListItemButton>
                          <Divider component="li" />
                        </React.Fragment>
                      ))}
                    </List>
                  </Paper>
                )}
              </Box>
            </ClickAwayListener>
          </Box>
        </Container>
      </Box>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: 300 } }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Header;
